// serviceWorkerRegistration.js

// Check for Instagram browser and Facebook IAB at the very beginning
const isInstagramBrowser = /instagram/i.test(navigator.userAgent) || /fb_iab/i.test(navigator.userAgent);

// Log detection to make debugging easier
if (isInstagramBrowser) {
  console.log('🚫 Instagram/Facebook IAB browser detected - Service Worker functionality will be disabled');
  
  // Kill the stale worker for Instagram only
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg) {
        console.log('[SW] Unregistering stale worker for Instagram');
        reg.unregister();
      }
    });
  }
}

// Determine if we're running on localhost
const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

// Create placeholder dummy functions for Instagram browser
const dummyRegister = (config) => {
  console.log('Service Worker registration skipped for Instagram browser');
  return Promise.resolve(null);
};

const dummyUnregister = () => {
  console.log('Service Worker unregistration skipped for Instagram browser');
  return Promise.resolve(null);
};

function getRegistrationScriptUrl(registration) {
  return String(
    registration?.active?.scriptURL
    || registration?.waiting?.scriptURL
    || registration?.installing?.scriptURL
    || ''
  );
}

async function cleanupLegacyServiceWorkers() {
  if (!('serviceWorker' in navigator) || typeof navigator.serviceWorker.getRegistrations !== 'function') {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  const legacyRegistrations = registrations.filter((registration) =>
    /\/push-sw\.js(?:\?|$)/i.test(getRegistrationScriptUrl(registration))
  );

  if (legacyRegistrations.length === 0) {
    return;
  }

  await Promise.all(legacyRegistrations.map(async (registration) => {
    const scriptUrl = getRegistrationScriptUrl(registration);
    try {
      console.warn('[SW Registration] Removing legacy push worker registration:', scriptUrl || registration.scope);
      await registration.unregister();
    } catch (error) {
      console.warn('[SW Registration] Failed to remove legacy push worker registration:', scriptUrl || registration.scope, error);
    }
  }));
}

/**
 * Safe SW register:
 * – won't crash if `registration.installing` is undefined
 * – won't postMessage until worker is **activated**
 * – no-op if the currently-controlling worker is already this revision.
 */
async function registerValidSW(swUrl, config) {
  try {
    const registration = await navigator.serviceWorker.register(swUrl);
    attachUpdateHandlers(registration, config);  // ✅ always attach
    return registration;
  } catch (err) {
    console.error('[SW] registration failed:', err);
  }
}

function attachUpdateHandlers(registration, config) {
  registration.onupdatefound = () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.onstatechange = () => {
      if (installing.state !== 'installed') return;
      if (navigator.serviceWorker.controller) {
        window.dispatchEvent(new CustomEvent('swUpdateFound', { detail: { registration } }));
      } else {
        config?.onSuccess?.(registration);
      }
    };
  };
}

// Real implementation of checkValidServiceWorker
function checkValidServiceWorker(swUrl, config) {
  console.log('🔍 Checking for valid service worker at:', swUrl);
  return fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        console.warn('⚠️ No service worker found. Reloading the page.');
        // Add check before accessing navigator.serviceWorker
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then((registration) => {
            registration.unregister().then(() => {
              console.log('🔄 Unregistered old service worker.');
              window.location.reload();
            });
          });
        } else {
           console.warn('🚫 ServiceWorker not supported, cannot unregister.');
        }
      } else {
        // Service worker found. Proceed as normal.
        console.log('✅ Valid service worker found. Registering...');
        return registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      console.log(
        '🛑 No internet connection found. App is running in offline mode.',
        error
      );
      throw error;
    });
}

// Real implementation of register
function realRegister(config) {
  return new Promise((resolve, reject) => {
    // Add log to check navigator.serviceWorker support status
    const swSupported = 'serviceWorker' in navigator;
    console.log(`[realRegister] Checking Service Worker support: ${swSupported}`);

    if (swSupported) {
      console.log('🌐 Service Worker registration starting...');

      const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
      if (publicUrl.origin !== window.location.origin) {
        console.warn('⚠️ Different origin – not registering SW');
        // Resolve with null or a specific indicator if registration is skipped
        return resolve(null); 
      }

      // Wrap registration logic in a function
      const doRegistration = () => {
        const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
        console.log('📝 Service Worker URL:', swUrl);

        let registrationPromise; // Variable to hold the promise

        if (isLocalhost) {
          // This is running on localhost. Let's check if a service worker still exists or not.
          console.log('🏠 Localhost detected. Checking for valid service worker.');
          registrationPromise = checkValidServiceWorker(swUrl, config);

          // Add some additional logging to localhost, pointing developers to the
          // service worker/PWA documentation.
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(() => {
              console.log(
                '✅ This web app is being served cache-first by a service worker.'
              );
            });
          }
        } else {
          // Is not localhost. Just register service worker
          console.log('🌐 Non-localhost environment. Registering service worker.');
          registrationPromise = registerValidSW(swUrl, config);
        }

        // Resolve or reject the main promise based on the registration outcome
        if (registrationPromise) {
            registrationPromise.then(resolve).catch(reject);
        } else {
            // Should not happen if logic is correct, but handle just in case
            reject(new Error("Registration promise was not created."));
        }
      };

      // Run immediately if load event already fired, otherwise add listener
      if (document.readyState === 'complete') {
        console.log('[realRegister] Document already loaded, running registration immediately.');
        doRegistration();
      } else {
        console.log('[realRegister] Document not loaded yet, adding load event listener for registration.');
        window.addEventListener('load', doRegistration);
      }
    } else {
      console.log('🛠️ Service Worker not registered (development mode or unsupported).', {
        environment: process.env.NODE_ENV,
        serviceWorkerSupported: 'serviceWorker' in navigator,
      });
      reject(new Error('Service Worker not supported'));
    }
  });
}

// Real implementation of unregister
function realUnregister() {
  if ('serviceWorker' in navigator && navigator.serviceWorker) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister().then(() => {
          console.log('🔄 Service Worker unregistered successfully.');
        });
      })
      .catch((error) => {
        console.error('❌ Error during service worker unregistration:', error);
      });
  } else {
    console.log('🚫 Service Worker not supported in this browser.');
  }
}

// Export either the dummy functions or the real ones based on browser detection
// Always use the real implementation on localhost so we can test push / cache
const useRealSW = !isInstagramBrowser && (process.env.NODE_ENV === 'production' || isLocalhost);

// Original export functions without the guard
const originalRegister = useRealSW ? realRegister   : dummyRegister;
const originalUnregister = useRealSW ? realUnregister : dummyUnregister;

// Exported function with idempotent pattern
export function register(config) {
  // Use idempotent pattern: check for existing registration first
  return cleanupLegacyServiceWorkers()
    .catch(error => {
      console.warn('[SW Registration] Legacy cleanup failed, continuing with registration:', error);
    })
    .then(() => navigator.serviceWorker.getRegistration())
    .then(existingRegistration => {
      if (existingRegistration) {
        console.log('[SW Registration] Service worker already registered, returning existing registration');
        attachUpdateHandlers(existingRegistration, config); // ✅ detect updates on subsequent loads
        existingRegistration.update();                      // ✅ ping for updates now
        
        // ✅ Prompt if a worker is already waiting (web UX polish)
        if (existingRegistration.waiting) {
          console.log('[SW Registration] Found waiting worker, dispatching update event');
          window.dispatchEvent(new CustomEvent('swUpdateFound', { detail: { registration: existingRegistration } }));
        }
        
        return existingRegistration;
      }
      
      console.log('[SW Registration] No existing registration found, starting new registration...');
      return originalRegister(config);
    })
    .catch(error => {
      console.error('[SW Registration] Error during idempotent registration:', error);
      // Fall back to direct registration if getRegistration fails
      return originalRegister(config);
    });
}

// Export unregister as well (no guard needed here usually, but keep export consistent)
export const unregister = originalUnregister;

/**
 * ❶ Tell an *active* waiting worker to become the controller.
 *    – only if it is really there
 *    – only if `postMessage` is legal in the current state
 */
export function forceSWUpdate(registration) {
  try {
    if (
      registration &&
      registration.waiting &&
      navigator.serviceWorker.controller &&       // page already controlled
      registration.waiting.state === 'installed'  // safe state
    ) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Let the controllerchange handler decide whether to reload
    } else {
      console.log('[SW] No waiting worker – skip update call');
    }
  } catch (err) {
    console.warn('[SW] Could not postMessage to waiting worker:', err);
  }
}
